'use client'

import type { ReactNode } from 'react'
import { UseControllerProps } from 'react-hook-form'

import { Label } from '@radix-ui/react-form'

import { FormRowContainer, RedText } from '../styled'
import { TField, TFieldValidator } from '../types'
import ErrorRow from './error-row'

export const RequiredStar = () => <RedText>*</RedText>

export const IsFieldRequired = (field: TField) => {
    if (!('validation' in field) || !field.validation)
        return false

    const required = field.validation.required
    return typeof required === 'object' && required !== null ? !!required.value : !!required
}

export const IsFieldDisabled = (field: TField) =>
    'disabled' in field ? Boolean(field.disabled) : false

/**
 * Controller rules of a field. `required` is left out when the field is not required right now
 * (a disabled field cannot be filled in); `extraValidate` is merged with the field's own `validate`
 * instead of replacing it.
 */
export const getFieldRules = (
    field: TField,
    required: boolean,
    extraValidate?: Record<string, TFieldValidator>
): UseControllerProps['rules'] => {
    const { required: requiredRule, validate, ...rules } = ('validation' in field && field.validation) || {}

    const validators = {
        ...(typeof validate === 'function' ? { validate } : validate),
        ...extraValidate,
    }

    return {
        ...rules,
        ...(required ? { required: requiredRule } : {}),
        ...(typeof validate === 'function' && !extraValidate
            ? { validate }
            : Object.keys(validators).length > 0 ? { validate: validators } : {}),
    }
}

type TFieldLabelProps = {
    htmlFor: string
    label: string
    required: boolean
    className?: string
}

export const FieldLabel = ({ htmlFor, label, required, className }: TFieldLabelProps) => (
    <Label htmlFor={htmlFor} className={className}>
        {label}
        {required && <RequiredStar />}
    </Label>
)

type TFieldRowProps = {
    fieldName: string
    label: string
    required: boolean
    children: ReactNode
    labelWidth?: string
    className?: string
    /** id of the labelled control, `fieldName` by default */
    htmlFor?: string
    /** the label is rendered after the control and floats over it */
    labelTransition?: boolean
    /** adds the `filled` class to the label */
    filled?: boolean
    /** the field renders its label itself (or has none) */
    hideLabel?: boolean
}

/** Row of one form field: label with the required star, the control and the error message. */
export const FieldRow = ({
    fieldName,
    label,
    required,
    children,
    labelWidth,
    className,
    htmlFor,
    labelTransition,
    filled,
    hideLabel,
}: TFieldRowProps) => {
    const labelElement = !hideLabel && (
        <FieldLabel
            htmlFor={htmlFor ?? fieldName}
            label={label}
            required={required}
            className={filled ? 'filled' : undefined}
        />
    )

    return (
        <FormRowContainer
            name={fieldName}
            $labelWidth={labelWidth}
            className={['form-item', className, labelTransition && 'form-label-transition'].filter(Boolean).join(' ')}
        >
            {!labelTransition && labelElement}
            {children}
            {labelTransition && labelElement}

            <ErrorRow fieldName={fieldName} label={label} />
        </FormRowContainer>
    )
}
